import { useLatest } from '@unifyapps/hooks/useLatest';
import { keyBy } from 'lodash';
import { useEffect } from 'react';
import type { useRegistryContext } from '../../../context/RegistryProvider';
import type { useGetGlobalAppStateStoreState } from '../../app/GlobalAppStateStore';
import { getInitialPageVariablesState } from '../../app/GlobalAppStateStore/utils/getInitialPageVariablesState';
import type { createGlobalStateStoreSelectors } from '../selectors';
import type { BaseGlobalStateStoreProviderProps } from '../types';
import { getInitialBlockState } from '../utils/getInitialBlockState';

type Store = ReturnType<typeof createGlobalStateStoreSelectors>;
type Registry = ReturnType<typeof useRegistryContext>['registry'];
type GetAppState = ReturnType<typeof useGetGlobalAppStateStoreState>['getGlobalAppStateStoreState'];

export function useGlobalStateStoreImperativeRef({
  store,
  registry,
  globalStateStoreRef,
  getGlobalAppStateStoreState,
  componentOverrides,
}: {
  store: Store;
  registry: Registry;
  globalStateStoreRef: BaseGlobalStateStoreProviderProps['globalStateStoreRef'];
  getGlobalAppStateStoreState: GetAppState;
  // forwarded so imperative block updates merge override deltas the same way the mount-time path does
  componentOverrides: BaseGlobalStateStoreProviderProps['componentOverrides'];
}) {
  const { ref: latestComponentOverridesRef } = useLatest(componentOverrides);

  useEffect(() => {
    if (globalStateStoreRef) {
      globalStateStoreRef.current = {
        getBlockRef: (blockId) => store.getState().blockRefs[blockId],
        updateBlocks: ({ added, changed, deleted }) => {
          const blocksToUpdate = [...added, ...changed];
          const keyByBlocks = keyBy(blocksToUpdate, 'id');
          const blockIdsToDelete = deleted.map((block) => block.id);

          const initialBlocksState = getInitialBlockState({
            blocks: keyByBlocks,
            registry,
            componentOverrides: latestComponentOverridesRef.current,
          });
          store.getState().actions.setBlocksState(initialBlocksState);
          store.getState().actions.removeBlocksState(blockIdsToDelete);
        },
        updatePageVariables: ({ added, changed, deleted }) => {
          const variablesToUpdate = [...added, ...changed];
          const keyByVariables = keyBy(variablesToUpdate, 'id');
          const variableIdsToDelete = deleted.map((v) => v.id);
          const currentAppState = getGlobalAppStateStoreState();
          const currentState = store.getState();

          const initialVariablesState = getInitialPageVariablesState({
            pageVariables: keyByVariables,
            appState: currentAppState,
            blocks: currentState.blocks,
            pageInputs: currentState.pageInputs,
            pageParams: currentState.pageParams,
          });

          store.getState().actions.setPageVariablesState(initialVariablesState);
          variableIdsToDelete.forEach((id) => {
            store.getState().actions.removePageVariableState(id);
          });
        },
      };
    }
  }, [
    globalStateStoreRef,
    registry,
    store,
    getGlobalAppStateStoreState,
    latestComponentOverridesRef,
  ]);
}
